/**
 * Chat Service
 * Handles AI chat conversations, plans, tasks, and Jira exports
 */
import api from './api';

const chatService = {
  // ==================== Conversations ====================
  /**
   * Get all conversations
   */
  getConversations: async (params = {}) => {
    const response = await api.get('/chat/conversations', { params });
    return response.data;
  },

  /**
   * Get conversation by ID
   */
  getConversation: async (conversationId) => {
    const response = await api.get(`/chat/conversations/${conversationId}`);
    return response.data;
  },

  /**
   * Create new conversation
   */
  createConversation: async (conversationData) => {
    const response = await api.post('/chat/conversations', conversationData);
    return response.data;
  },

  /**
   * Delete conversation
   */
  deleteConversation: async (conversationId) => {
    const response = await api.delete(`/chat/conversations/${conversationId}`);
    return response.data;
  },

  /**
   * Send message in conversation
   */
  sendMessage: async (conversationId, message) => {
    const response = await api.post(`/chat/conversations/${conversationId}/messages`, {
      content: message
    });
    return response.data;
  },

  /**
   * Get messages for conversation
   */
  getMessages: async (conversationId, params = {}) => {
    const response = await api.get(`/chat/conversations/${conversationId}/messages`, { params });
    return response.data;
  },

  // ==================== Plans ====================
  /**
   * Get all plans
   */
  getPlans: async (params = {}) => {
    const response = await api.get('/chat/plans', { params });
    return response.data;
  },

  /**
   * Get plan by UUID
   */
  getPlan: async (planUuid) => {
    const response = await api.get(`/chat/plans/${planUuid}`);
    return response.data;
  },

  /**
   * Create new plan
   */
  createPlan: async (planData) => {
    const response = await api.post('/chat/plans', planData);
    return response.data;
  },

  /**
   * Update plan
   */
  updatePlan: async (planUuid, planData) => {
    const response = await api.put(`/chat/plans/${planUuid}`, planData);
    return response.data;
  },

  /**
   * Delete plan
   */
  deletePlan: async (planUuid) => {
    const response = await api.delete(`/chat/plans/${planUuid}`);
    return response.data;
  },

  /**
   * Export plan to Jira
   */
  exportPlanToJira: async (planUuid, jiraConfig = {}) => {
    const response = await api.post(`/chat/plans/${planUuid}/export/jira`, jiraConfig);
    return response.data;
  },

  // ==================== Tasks ====================
  /**
   * Get all tasks
   */
  getTasks: async (params = {}) => {
    const response = await api.get('/chat/tasks', { params });
    return response.data;
  },

  /**
   * Get task by UUID
   */
  getTask: async (taskUuid) => {
    const response = await api.get(`/chat/tasks/${taskUuid}`);
    return response.data;
  },

  /**
   * Create new task
   */
  createTask: async (taskData) => {
    const response = await api.post('/chat/tasks', taskData);
    return response.data;
  },

  /**
   * Update task
   */
  updateTask: async (taskUuid, taskData) => {
    const response = await api.put(`/chat/tasks/${taskUuid}`, taskData);
    return response.data;
  },

  /**
   * Delete task
   */
  deleteTask: async (taskUuid) => {
    const response = await api.delete(`/chat/tasks/${taskUuid}`);
    return response.data;
  },

  /**
   * Convert task (to different type or action item)
   */
  convertTask: async (taskUuid, conversionConfig) => {
    const response = await api.post(`/chat/tasks/${taskUuid}/convert`, conversionConfig);
    return response.data;
  },

  /**
   * Export task to Jira
   */
  exportTaskToJira: async (taskUuid, jiraConfig = {}) => {
    const response = await api.post(`/chat/tasks/${taskUuid}/export/jira`, jiraConfig);
    return response.data;
  },

  /**
   * Get tasks for plan
   */
  getTasksForPlan: async (planUuid, params = {}) => {
    const response = await api.get(`/chat/plans/${planUuid}/tasks`, { params });
    return response.data;
  },

  /**
   * Add task to plan
   */
  addTaskToPlan: async (planUuid, taskData) => {
    const response = await api.post(`/chat/plans/${planUuid}/tasks`, taskData);
    return response.data;
  },

  // ==================== AI Suggestions ====================
  /**
   * Get AI suggestions for conversation
   */
  getSuggestions: async (conversationId) => {
    const response = await api.get(`/chat/conversations/${conversationId}/suggestions`);
    return response.data;
  },

  /**
   * Apply AI suggestion
   */
  applySuggestion: async (conversationId, suggestionId) => {
    const response = await api.post(`/chat/conversations/${conversationId}/suggestions/${suggestionId}/apply`);
    return response.data;
  },

  // ==================== Search ====================
  /**
   * Search conversations
   */
  searchConversations: async (query, params = {}) => {
    const response = await api.get('/chat/conversations/search', {
      params: { q: query, ...params }
    });
    return response.data;
  },

  /**
   * Search tasks
   */
  searchTasks: async (query, params = {}) => {
    const response = await api.get('/chat/tasks/search', {
      params: { q: query, ...params }
    });
    return response.data;
  }
};

export default chatService;
